import React, { useCallback, useMemo, useState } from "react";
import { Grid, GridColumn as Column, GridCellProps } from "@progress/kendo-react-grid";

interface FormulasValues {
  charge: string;
  quantity: string;
  quantityOfDataItems: string;
  purchaseText: string;
  skuId: string;
}

interface TieredPricingModel {
  defaultFormulas: FormulasValues;
  tiers: { bottom: number; formulas: Partial<FormulasValues> }[];
}

interface TierRow {
  _rowId: number;
  tier: string;
  tierDisplay?: string;
  charge: string;
  quantity: string;
  quantityOfDataItems: string;
  purchaseText: string;
  skuId: string;
  isDefault: boolean;
  index: number;
}

interface EditDraft {
  tier: string;
  originalTier?: string;
  charge: string;
  quantity: string;
  quantityOfDataItems: string;
  purchaseText: string;
  skuId: string;
  isDefault: boolean;
  index: number;
  isNew?: boolean;
}

const EMPTY_FORMULAS: FormulasValues = { charge: "", quantity: "", quantityOfDataItems: "", purchaseText: "", skuId: "" };

export interface CellError {
  /** "default" or tier index (0-based) */
  row: number | "default";
  field: keyof FormulasValues | "tier";
  message: string;
}

interface TieredPricingEditorProps {
  json: string;
  onChange: (json: string) => void;
  errors?: CellError[];
  onValidate?: () => void;
  isValidating?: boolean;
  toolbarExtra?: React.ReactNode;
  toolbarBefore?: React.ReactNode;
}

const TieredPricingEditor: React.FC<TieredPricingEditorProps> = ({ json, onChange, errors = [], onValidate, isValidating, toolbarExtra, toolbarBefore }) => {
  const [draft, setDraft] = useState<EditDraft | null>(null);

  const model = useMemo<TieredPricingModel>(() => {
    try {
      const parsed = JSON.parse(json);
      return {
        defaultFormulas: parsed.defaultFormulas ?? { ...EMPTY_FORMULAS },
        tiers: Array.isArray(parsed.tiers) ? parsed.tiers : [],
      };
    } catch {
      return { defaultFormulas: { ...EMPTY_FORMULAS }, tiers: [] };
    }
  }, [json]);

  const emit = (updated: TieredPricingModel) => onChange(JSON.stringify(updated, null, 4));

  const rows = useMemo<TierRow[]>(() => {
    const defaults = model.defaultFormulas ?? EMPTY_FORMULAS;
    const result: TierRow[] = [
      {
        _rowId: 0,
        tier: "default",
        charge: defaults.charge,
        quantity: defaults.quantity,
        quantityOfDataItems: defaults.quantityOfDataItems,
        purchaseText: defaults.purchaseText,
        skuId: defaults.skuId,
        isDefault: true,
        index: -1,
      },
    ];
    model.tiers.forEach((t, i) => {
      const nextBottom = model.tiers[i + 1]?.bottom;
      const fmt = (n: number) => n.toLocaleString();
      const tierDisplay = nextBottom != null ? `${fmt(t.bottom)} – ${fmt(nextBottom - 1)}` : `${fmt(t.bottom)}+`;
      result.push({
        _rowId: i + 1,
        tier: String(t.bottom),
        tierDisplay,
        charge: t.formulas?.charge ?? "",
        quantity: t.formulas?.quantity ?? "",
        quantityOfDataItems: t.formulas?.quantityOfDataItems ?? "",
        purchaseText: t.formulas?.purchaseText ?? "",
        skuId: t.formulas?.skuId ?? "",
        isDefault: false,
        index: i,
      });
    });
    return result;
  }, [model]);

  const enterEdit = (dataItem: TierRow) => {
    setDraft({
      tier: dataItem.tier,
      originalTier: dataItem.tier,
      charge: dataItem.charge,
      quantity: dataItem.quantity,
      quantityOfDataItems: dataItem.quantityOfDataItems,
      purchaseText: dataItem.purchaseText,
      skuId: dataItem.skuId,
      isDefault: dataItem.isDefault,
      index: dataItem.index,
    });
  };

  const addTier = () => {
    setDraft({
      tier: model.tiers.length === 0 ? "0" : "",
      charge: "",
      quantity: "",
      quantityOfDataItems: "",
      purchaseText: "",
      skuId: "",
      isDefault: false,
      index: model.tiers.length,
      isNew: true,
    });
  };

  const cancelEdit = () => setDraft(null);

  const confirmEdit = () => {
    if (!draft) return;
    if (draft.isDefault) {
      emit({
        ...model,
        defaultFormulas: {
          charge: draft.charge,
          quantity: draft.quantity,
          quantityOfDataItems: draft.quantityOfDataItems,
          purchaseText: draft.purchaseText,
          skuId: draft.skuId,
        },
      });
    } else {
      const bottom = Number(draft.tier);
      if (!Number.isInteger(bottom) || bottom < 0) {
        alert("Tier bottom must be a non-negative integer.");
        return;
      }
      // Check for duplicate bottom values
      const duplicate = model.tiers.some((t, i) => t.bottom === bottom && i !== draft.index);
      if (duplicate) {
        alert(`A tier with bottom ${bottom} already exists.`);
        return;
      }

      let tiers: TieredPricingModel["tiers"];
      if (draft.isNew) {
        tiers = [...model.tiers, {
          bottom,
          formulas: {
            charge: draft.charge,
            quantity: draft.quantity,
            quantityOfDataItems: draft.quantityOfDataItems,
            purchaseText: draft.purchaseText,
            skuId: draft.skuId,
          },
        }];
      } else {
        tiers = [...model.tiers];
        tiers[draft.index] = {
          bottom,
          formulas: {
            charge: draft.charge,
            quantity: draft.quantity,
            quantityOfDataItems: draft.quantityOfDataItems,
            purchaseText: draft.purchaseText,
            skuId: draft.skuId,
          },
        };
      }
      // Sort by bottom to maintain contiguous coverage
      tiers.sort((a, b) => a.bottom - b.bottom);
      emit({ ...model, tiers });
    }
    setDraft(null);
  };

  const deleteTier = (dataItem: TierRow) => {
    if (dataItem.isDefault) return;
    const range = dataItem.tierDisplay ?? `${dataItem.tier}+`;
    if (!confirm(`Delete tier ${range}?`)) return;
    let tiers = model.tiers.filter((_, i) => i !== dataItem.index);
    // Ensure first tier starts at 0
    if (tiers.length > 0 && tiers[0].bottom !== 0) {
      tiers = [{ ...tiers[0], bottom: 0 }, ...tiers.slice(1)];
    }
    emit({ ...model, tiers });
  };

  const getCellError = (dataItem: TierRow, field: string): string | undefined => {
    const rowKey = dataItem.isDefault ? "default" : dataItem.index;
    return errors.find((e) => e.row === rowKey && e.field === field)?.message;
  };

  const getDraftFieldError = (field: string): string | undefined => {
    if (!draft) return undefined;
    const rowKey = draft.isDefault ? "default" : draft.index;
    return errors.find((e) => e.row === rowKey && e.field === field)?.message;
  };

  const EditableCell = useCallback((props: GridCellProps) => {
    const field = props.field || "";
    const value = props.dataItem[field] ?? "";
    const isDefault = props.dataItem.isDefault;
    const cellError = getCellError(props.dataItem, field);
    const errorStyle = cellError ? { border: "2px solid #dc3545", borderRadius: 4 } : {};

    if (field === "tier" && isDefault) {
      return <td>default</td>;
    }

    if (field === "tier") {
      return <td style={{ whiteSpace: "nowrap", ...errorStyle }} title={cellError ?? undefined}>{props.dataItem.tierDisplay ?? value}</td>;
    }

    return <td style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...errorStyle }} title={cellError ?? value}>{value}</td>;
  }, [errors]);

  const CommandCell = useCallback((props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => enterEdit(dataItem)}
            disabled={draft !== null}
          >
            Edit
          </button>
          {!dataItem.isDefault && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => deleteTier(dataItem)}
              disabled={draft !== null}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    );
  }, [draft, model]);

  return (
    <>
      {errors.length > 0 && (
        <div className="alert alert-danger py-2 mb-2">
          <strong>Validation errors ({errors.length}):</strong>
          <ul className="mb-0 ps-3" style={{ maxHeight: "7.5em", overflowY: "auto" }}>
            {errors.map((e, i) => {
              let label: string;
              if (e.row === "default") {
                label = "Default";
              } else {
                const row = rows.find((r) => r.index === e.row);
                label = row?.tierDisplay ? `Tier ${row.tierDisplay}` : `Tier ${e.row}`;
              }
              return (
                <li key={i}>
                  {label} — {e.field}: {e.message}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div className="mb-2 d-flex gap-2 align-items-center">
        {toolbarBefore}
        <button className="btn btn-success btn-sm" onClick={addTier} disabled={draft !== null}>
          + Add Tier
        </button>
        {onValidate && (
          <button className="btn btn-outline-primary btn-sm" onClick={onValidate} disabled={isValidating}>
            {isValidating ? "Validating…" : "Validate Formulas"}
          </button>
        )}
        {toolbarExtra}
      </div>
      <Grid
        className="sysadmin-grid"
        data={rows}
        dataItemKey="_rowId"
        resizable={true}
        style={{ width: "100%", height: "calc(100vh - 200px)" }}
        scrollable="scrollable"
      >
        <Column field="tier" title="Tier" width={160} cells={{ data: EditableCell }} />
        <Column field="charge" title="Charge" width={150} cells={{ data: EditableCell }} />
        <Column field="quantity" title="Quantity" width={150} cells={{ data: EditableCell }} />
        <Column field="quantityOfDataItems" title="Quantity of Data Items" width={200} cells={{ data: EditableCell }} />
        <Column field="purchaseText" title="Purchase Text" cells={{ data: EditableCell }} />
        <Column title="Actions" width={200} filterable={false} sortable={false} cells={{ data: CommandCell }} />
      </Grid>

      {draft && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog" style={{ maxWidth: "1000px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{draft.isNew ? "Add Tier" : draft.isDefault ? "Edit Default Formulas" : "Edit Tier"}</h5>
                <button type="button" className="btn-close" onClick={cancelEdit} />
              </div>
              <div className="modal-body">
                {!draft.isDefault && (
                  <div className="mb-3">
                    <label className="form-label">Tier Bottom</label>
                    <input type="text" className={`form-control${getDraftFieldError("tier") ? " is-invalid" : ""}`} value={draft.tier} onChange={(e) => setDraft({ ...draft, tier: e.target.value })} disabled={(draft.originalTier === "0" && !draft.isNew) || (draft.isNew && model.tiers.length === 0)} style={(draft.originalTier === "0" && !draft.isNew) || (draft.isNew && model.tiers.length === 0) ? { backgroundColor: "#e9ecef" } : undefined} />
                    {getDraftFieldError("tier") && <div className="invalid-feedback">{getDraftFieldError("tier")}</div>}
                    {draft.originalTier === "0" && !draft.isNew && (
                      <small className="text-muted">Tiers must cover usage range from 0 to +&infin; The range for the first tier must start from 0. You cannot change this value.</small>
                    )}
                    {draft.isNew && model.tiers.length === 0 && (
                      <small className="text-muted">Tiers must cover space from 0 to +&infin;. First tier bottom is always 0.</small>
                    )}
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Charge</label>
                  <textarea className={`form-control${getDraftFieldError("charge") ? " is-invalid" : ""}`} rows={1} value={draft.charge} onChange={(e) => setDraft({ ...draft, charge: e.target.value })} />
                  {getDraftFieldError("charge") && <div className="invalid-feedback">{getDraftFieldError("charge")}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Quantity</label>
                  <textarea className={`form-control${getDraftFieldError("quantity") ? " is-invalid" : ""}`} rows={1} value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} />
                  {getDraftFieldError("quantity") && <div className="invalid-feedback">{getDraftFieldError("quantity")}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Quantity of Data Items</label>
                  <textarea className={`form-control${getDraftFieldError("quantityOfDataItems") ? " is-invalid" : ""}`} rows={1} value={draft.quantityOfDataItems} onChange={(e) => setDraft({ ...draft, quantityOfDataItems: e.target.value })} />
                  {getDraftFieldError("quantityOfDataItems") && <div className="invalid-feedback">{getDraftFieldError("quantityOfDataItems")}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Purchase Text</label>
                  <textarea className={`form-control${getDraftFieldError("purchaseText") ? " is-invalid" : ""}`} rows={1} value={draft.purchaseText} onChange={(e) => setDraft({ ...draft, purchaseText: e.target.value })} />
                  {getDraftFieldError("purchaseText") && <div className="invalid-feedback">{getDraftFieldError("purchaseText")}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={confirmEdit}>OK</button>
                <button className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TieredPricingEditor;
